import { useState, useEffect, useCallback } from "react";
import { Loader2, Search, X, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { DataTablePagination } from "@/components/ui/data-table-pagination";
import { toast } from "@/components/ui/use-toast";
import { JournalEntryDTO } from "@/types/journalEntry";
import { journalEntryService } from "@/services/journalEntryService";
import JournalEntryItem from "@/components/admin/journal/JournalEntryItem";
import {
  JournalActionFields,
  JournalActionValue,
  toReminderIso,
} from "@/components/admin/journal/JournalActionFields";

const PAGE_SIZE = 20;

/** Which kind of entry the feed is showing. */
type Scope = "all" | "events" | "personal";

const SCOPES: { value: Scope; label: string }[] = [
  { value: "all", label: "All" },
  { value: "events", label: "About an event" },
  { value: "personal", label: "Personal reminders" },
];

/** `standalone` is tri-state on the API: undefined leaves both kinds in. */
const standaloneFor = (scope: Scope): boolean | undefined =>
  scope === "all" ? undefined : scope === "personal";

export default function JournalFeed() {
  const [entries, setEntries] = useState<JournalEntryDTO[]>([]);
  const [loading, setLoading] = useState(true);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [scope, setScope] = useState<Scope>("all");

  const [newContent, setNewContent] = useState("");
  const [newFollowUp, setNewFollowUp] = useState<JournalActionValue>({
    action: "",
    reminderAt: "",
  });
  const [saving, setSaving] = useState(false);

  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Debounce the free-text search so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset to first page whenever a filter changes.
  useEffect(() => {
    setPage(0);
  }, [createdFrom, createdTo, scope]);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const data = await journalEntryService.list({
        standalone: standaloneFor(scope),
        search: search || undefined,
        createdFrom: createdFrom || undefined,
        createdTo: createdTo || undefined,
        page,
        size: PAGE_SIZE,
      });
      setEntries(data.content);
      setTotalPages(data.page.totalPages);
    } catch {
      toast({
        title: "Failed to load journal entries",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [search, createdFrom, createdTo, scope, page]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleUpdated = (updated: JournalEntryDTO) => {
    setEntries((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
  };

  const handleDeleted = (deletedId: string) => {
    setEntries((prev) => prev.filter((e) => e.id !== deletedId));
  };

  const handleAdd = async () => {
    if (!newContent.trim()) {
      toast({ title: "Entry can't be empty", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      // No eventId: this is a note the admin is leaving themselves.
      await journalEntryService.create({
        content: newContent.trim(),
        action: newFollowUp.action.trim() || null,
        reminderAt: toReminderIso(newFollowUp.reminderAt),
      });
      setNewContent("");
      setNewFollowUp({ action: "", reminderAt: "" });
      toast({
        title: "Reminder added",
        description: newFollowUp.reminderAt
          ? "The admins will be reminded at the time you picked."
          : undefined,
      });
      if (page !== 0) {
        setPage(0); // jump to newest; triggers refetch
      } else {
        fetchEntries();
      }
    } catch {
      toast({ title: "Failed to add reminder", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const clearFilters = () => {
    setSearchInput("");
    setCreatedFrom("");
    setCreatedTo("");
    setScope("all");
  };

  const hasFilters = searchInput || createdFrom || createdTo || scope !== "all";

  return (
    <div className="min-h-screen p-4 sm:p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Journal</h1>
          <p className="text-muted-foreground">
            Notes on events and reminders you set yourself, newest first.
          </p>
        </div>

        {/* Quick-add for a reminder that is not about any one event. */}
        <Card className="mb-0">
          <CardContent className="p-3 space-y-2">
            <Textarea
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              placeholder="Leave yourself a note…"
              rows={3}
            />
            <JournalActionFields
              idPrefix="new-personal-entry"
              value={newFollowUp}
              onChange={setNewFollowUp}
              disabled={saving}
            />
            <Button
              onClick={handleAdd}
              disabled={saving}
              className="w-full sm:w-auto"
            >
              {saving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Plus className="mr-2 h-4 w-4" />
              )}
              Add Reminder
            </Button>
          </CardContent>
        </Card>

        {/* Filters */}
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {SCOPES.map((option) => (
              <Button
                key={option.value}
                type="button"
                size="sm"
                variant={scope === option.value ? "default" : "outline"}
                aria-pressed={scope === option.value}
                className={cn(scope === option.value && "pointer-events-none")}
                onClick={() => setScope(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search entries…"
              className="pl-9"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 space-y-1">
              <Label htmlFor="from" className="text-xs text-muted-foreground">
                From
              </Label>
              <Input
                id="from"
                type="date"
                value={createdFrom}
                onChange={(e) => setCreatedFrom(e.target.value)}
              />
            </div>
            <div className="flex-1 space-y-1">
              <Label htmlFor="to" className="text-xs text-muted-foreground">
                To
              </Label>
              <Input
                id="to"
                type="date"
                value={createdTo}
                onChange={(e) => setCreatedTo(e.target.value)}
              />
            </div>
          </div>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="mr-1.5 h-4 w-4" />
              Clear filters
            </Button>
          )}
        </div>

        {/* Feed */}
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : entries.length === 0 ? (
          <p className="text-center text-muted-foreground py-10">
            No journal entries found.
          </p>
        ) : (
          <div className="space-y-2">
            {entries.map((entry) => (
              <JournalEntryItem
                key={entry.id}
                entry={entry}
                showEventLink
                onUpdated={handleUpdated}
                onDeleted={handleDeleted}
              />
            ))}
          </div>
        )}

        <DataTablePagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
}
